import { useState, useEffect } from "react";
import { X, ExternalLink, MessageCircle, ThumbsUp, ThumbsDown, Send, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArticleModal = ({ article, isOpen, onClose, onAuthClick }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [fullText, setFullText] = useState("");
  const [loadingContent, setLoadingContent] = useState(true);
  const [extractedImage, setExtractedImage] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (article && isOpen) {
      fetchComments();
      fetchFullArticle();
    }
    if (!isOpen) {
      setFullText("");
      setLoadingContent(true);
    }
  }, [article, isOpen]);

  const fetchFullArticle = async () => {
    setLoadingContent(true);
    try {
      const { data } = await axios.get(`${API}/article/extract`, {
        params: { url: article.url },
        timeout: 15000,
      });
      if (data.text) {
        setFullText(data.text);
      }
      if (data.top_image && !article.image_url) {
        setExtractedImage(data.top_image);
      }
    } catch (err) {
      console.error("Failed to extract article:", err);
      setFullText("");
    } finally {
      setLoadingContent(false);
    }
  };

  const fetchComments = async () => {
    try {
      const { data } = await axios.get(`${API}/comments`, { params: { article_url: article.url } });
      setComments(data.comments || []);
    } catch (err) { console.error("Failed to fetch modal comments:", err); }
  };

  const submitComment = async () => {
    if (!user) { onAuthClick(); return; }
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API}/comments`, {
        article_url: article.url,
        text: newComment.trim(),
        parent_comment_id: replyTo,
      }, { headers: getAuthHeader() });
      setNewComment("");
      setReplyTo(null);
      fetchComments();
    } catch (err) { console.error("Failed to submit modal comment:", err); }
  };

  const handleLike = async (commentId) => {
    if (!user) { onAuthClick(); return; }
    try {
      await axios.post(`${API}/comments/${commentId}/like`, {}, { headers: getAuthHeader() });
      fetchComments();
    } catch (err) { console.error("Failed to like comment:", err); }
  };

  const handleDislike = async (commentId) => {
    if (!user) { onAuthClick(); return; }
    try {
      await axios.post(`${API}/comments/${commentId}/dislike`, {}, { headers: getAuthHeader() });
      fetchComments();
    } catch (err) { console.error("Failed to dislike comment:", err); }
  };

  const displayImage = article?.image_url || extractedImage;
  const displayContent = fullText || article?.description || "";

  if (!isOpen || !article) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
      style={{ backgroundColor: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="article-modal-overlay"
    >
      <div
        className="relative w-full max-w-3xl mx-4 my-6 sm:my-10 rounded-2xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="article-modal"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full transition-colors"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", color: "#fff" }}
          data-testid="article-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Article Image */}
        {displayImage && (
          <div className="w-full aspect-video overflow-hidden">
            <img
              src={displayImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Article Content */}
        <div className="p-5 sm:p-8">
          {/* Category badge */}
          {article.category && (
            <span
              className="inline-block px-3 py-1 text-[10px] sm:text-xs font-bold tracking-wider uppercase rounded-full mb-4"
              style={{ backgroundColor: "var(--color-accent)", color: "#fff" }}
            >
              {article.category}
            </span>
          )}

          {/* Title */}
          <h2
            className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight leading-tight mb-4"
            style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}
            data-testid="article-modal-title"
          >
            {article.title}
          </h2>

          {/* Source & time */}
          <div className="flex items-center gap-3 text-sm mb-6 pb-4" style={{ color: "var(--color-text-dim)", borderBottom: "1px solid var(--color-border)" }}>
            <span className="font-bold" style={{ color: "var(--color-live)" }}>{article.source}</span>
            {article.published_at && (
              <>
                <span>•</span>
                <span>{new Date(article.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
              </>
            )}
          </div>

          {/* Full article text */}
          {loadingContent ? (
            <div className="flex items-center gap-3 py-8 justify-center" style={{ color: "var(--color-text-muted)" }}>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading full article...</span>
            </div>
          ) : (
            <div
              className="prose prose-sm max-w-none mb-6"
              style={{ color: "var(--color-text-muted)" }}
              data-testid="article-modal-content"
            >
              {displayContent.split("\n").map((paragraph, i) => (
                paragraph.trim() ? (
                  <p key={`p-${i}`} className="text-sm sm:text-base leading-relaxed mb-4" style={{ color: "var(--color-text-muted)" }}>
                    {paragraph}
                  </p>
                ) : null
              ))}
            </div>
          )}

          {/* Source credit */}
          <div
            className="flex items-center justify-between flex-wrap gap-3 p-4 rounded-xl mb-6"
            style={{ backgroundColor: "var(--color-bg)", border: "1px solid var(--color-border)" }}
          >
            <p className="text-xs sm:text-sm" style={{ color: "var(--color-text-muted)" }}>
              Originally published by <strong style={{ color: "var(--color-text)" }}>{article.source}</strong>
            </p>
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all hover:brightness-110"
              style={{ backgroundColor: "var(--color-accent)", color: "#fff" }}
              data-testid="visit-source-btn"
            >
              Visit Source <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Comments Section */}
          <div style={{ borderTop: "1px solid var(--color-border)" }} className="pt-6">
            <h3 className="text-base font-bold mb-4 flex items-center gap-2" style={{ color: "var(--color-text)" }}>
              <MessageCircle className="w-5 h-5" style={{ color: "var(--color-accent)" }} />
              Comments ({comments.length})
            </h3>

            {/* Comment input */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitComment()}
                placeholder={user ? (replyTo ? "Write a reply..." : "Share your thoughts...") : "Sign in to comment"}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm outline-none"
                style={{ backgroundColor: "var(--color-bg)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
                data-testid="comment-input"
              />
              <button
                onClick={submitComment}
                className="px-4 py-2.5 rounded-xl transition-all hover:brightness-110"
                style={{ backgroundColor: "var(--color-accent)", color: "#fff" }}
                data-testid="comment-submit"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>

            {replyTo && (
              <button onClick={() => setReplyTo(null)} className="text-xs mb-3 flex items-center gap-1 hover:underline" style={{ color: "var(--color-accent)" }}>
                <X className="w-3 h-3" /> Cancel reply
              </button>
            )}

            {/* Comments list */}
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {comments.length === 0 ? (
                <p className="text-sm text-center py-4" style={{ color: "var(--color-text-dim)" }}>No comments yet. Be the first!</p>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="p-3 rounded-xl"
                    style={{ backgroundColor: "var(--color-bg)", border: comment.parent_comment_id ? "1px solid var(--color-border)" : "none", marginLeft: comment.parent_comment_id ? "24px" : "0" }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold" style={{ color: "var(--color-accent)" }}>@{comment.user_name}</span>
                      <span className="text-[10px]" style={{ color: "var(--color-text-dim)" }}>
                        {new Date(comment.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm mb-2" style={{ color: "var(--color-text-muted)" }}>{comment.text}</p>
                    <div className="flex items-center gap-3">
                      <button onClick={() => handleLike(comment.id)} className="flex items-center gap-1 text-xs transition-colors hover:brightness-125" style={{ color: "var(--color-text-dim)" }}>
                        <ThumbsUp className="w-3 h-3" /> {comment.like_count || 0}
                      </button>
                      <button onClick={() => handleDislike(comment.id)} className="flex items-center gap-1 text-xs transition-colors hover:brightness-125" style={{ color: "var(--color-text-dim)" }}>
                        <ThumbsDown className="w-3 h-3" /> {comment.dislike_count || 0}
                      </button>
                      <button onClick={() => setReplyTo(comment.id)} className="text-xs font-bold hover:underline" style={{ color: "var(--color-accent)" }}>
                        Reply
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleModal;
